/**
 * Activity feed + note composer for LeadDetailPage (round7/lead-activity-
 * portal — portal half of P2-B; the backend half, round7/lead-activity-
 * backend, is already merged and live in DEV).
 *
 * Renders `lead.activityFeed` — a single chronological list DockPro-Backend
 * composes server-side from the lead's original submission, its
 * designState.versions, its notes, and its stageHistory (see that repo's
 * lib/lambda/utils/leadActivity.ts's composeActivityFeed() doc comment for
 * the exact composition/sort rules). This component renders the array
 * EXACTLY as given — submission pinned first, everything else ascending by
 * timestamp — rather than reversing it the way VersionHistoryPanel shows its
 * own (unrelated) versions list newest-first: a version list is something
 * you scan to pick one, so newest-first reads best; this is a work log read
 * top-to-bottom like a timeline, so oldest-to-newest (ending with whatever
 * just happened) is the more natural reading order. A freshly-added note
 * lands at the bottom, not the top.
 *
 * Each entry type shows whatever author information the backend actually
 * attaches to it — never a fabricated one:
 *   - submission: no authorKind/role field exists on this entry at all; badged
 *     "Homeowner" with no role (a homeowner never has an owner/rep role).
 *   - version: has `authorKind` (homeowner|builder) but no owner/rep role —
 *     badged the same way VersionHistoryPanel already badges its own rows,
 *     for visual consistency between the two.
 *   - note: has `authorRole` (owner|rep) but no separate authorKind field
 *     (a note is always builder-authored) — badged "Owner"/"Rep".
 *   - stage_change: carries only `byUserId` (a raw Cognito sub, not a
 *     display name) — badged generically "Builder" (only an authenticated
 *     builder-portal caller can move a lead's stage) rather than guessing
 *     owner/rep from data that isn't there.
 * None of the four surfaces a raw authorUserId/authoredBy/byUserId sub to
 * the builder — same posture as the rest of this page (e.g.
 * VersionHistoryPanel already doesn't surface `authoredBy` either), since a
 * bare UUID isn't meaningful to a human reader.
 *
 * A version entry's content is deliberately a short label ONLY — "Saved a
 * design version" plus its optional save label — linking to the existing
 * Version History panel (`#version-history`, an id LeadDetailPage wraps
 * around <VersionHistoryPanel> from the outside) rather than repeating that
 * panel's own view/restore affordances here. This is a real, keyboard-
 * operable, screen-reader-visible `<a href="#...">` — no JS scroll-into-view
 * hack needed for same-page anchors — and it's a LINK, not a duplicate: this
 * component never fetches or renders a version's own designState.
 *
 * No edit/delete UI for a note anywhere in this file, and there never should
 * be — POST .../notes is the only write path DockPro-Backend exposes for
 * `notes` (see that repo's portalCreateLeadNoteHandler.ts doc comment): a
 * correction is a new note, not a mutation of an old one.
 *
 * The note composer's error/success feedback uses the shared StatusMessage
 * component (Round 6 polish pattern — see StatusMessage.tsx's own doc
 * comment), same as every other form/action this repo migrated onto it
 * post-audit, rather than a bespoke `{error && <p>}` block.
 */
import type { FormEvent } from "react";
import type { ActivityFeedEntry } from "../lib/api";
import { MAX_NOTE_TEXT_LENGTH } from "../lib/api";
import { STAGE_LABELS } from "../lib/leads";
import { StatusMessage } from "./StatusMessage";
import ui from "../styles/ui.module.css";
import styles from "./ActivityFeedPanel.module.css";

interface Props {
  activityFeed: ActivityFeedEntry[];
  noteText: string;
  onNoteTextChange: (text: string) => void;
  onSubmitNote: (e: FormEvent) => void;
  isSubmittingNote: boolean;
  noteError: string | null;
  // Transient "Note added." confirmation — the caller clears this a few
  // seconds after success, mirroring SettingsPage's own `saved` flag/timeout
  // for its "Saved." StatusMessage.
  noteAdded: boolean;
}

export function ActivityFeedPanel({
  activityFeed, noteText, onNoteTextChange, onSubmitNote,
  isSubmittingNote, noteError, noteAdded,
}: Props) {
  const trimmedLength = noteText.trim().length;

  return (
    <section className={ui.card} aria-labelledby="activity-feed-heading">
      <h2 id="activity-feed-heading" className={ui.cardTitle}>Activity</h2>

      <form onSubmit={onSubmitNote} className={styles.noteForm}>
        <label htmlFor="lead-note-text" className={styles.noteLabel}>Add a note</label>
        <textarea
          id="lead-note-text"
          value={noteText}
          onChange={(e) => onNoteTextChange(e.target.value)}
          placeholder="Add a note for this lead…"
          rows={3}
          maxLength={MAX_NOTE_TEXT_LENGTH}
          disabled={isSubmittingNote}
          className={styles.noteTextarea}
        />
        <div className={styles.noteFormFooter}>
          <span className={styles.noteHint}>
            Notes are visible to everyone on your team and can&rsquo;t be edited or deleted once added.
          </span>
          <button
            type="submit"
            className={`${ui.button} ${ui.buttonSmall}`}
            disabled={isSubmittingNote || trimmedLength === 0}
          >
            {isSubmittingNote ? "Adding…" : "Add note"}
          </button>
        </div>
      </form>

      {noteAdded && (
        <StatusMessage variant="status" message="Note added." className={`${ui.status} ${styles.noteStatusMessage}`} />
      )}
      {noteError && (
        <StatusMessage variant="error" message={noteError} className={`${ui.error} ${styles.noteStatusMessage}`} />
      )}

      {activityFeed.length === 0 ? (
        <p className={ui.subtle}>No activity yet.</p>
      ) : (
        <ol className={styles.list}>
          {activityFeed.map((entry, index) => (
            <ActivityEntryRow key={entryKey(entry, index)} entry={entry} />
          ))}
        </ol>
      )}
    </section>
  );
}

function entryKey(entry: ActivityFeedEntry, index: number): string {
  switch (entry.type) {
    case "submission": return `submission-${index}`;
    case "version": return `version-${entry.versionId}`;
    case "note": return `note-${entry.noteId}`;
    // No stable id on a stage_change entry (see StageHistoryEntry's shape) —
    // its (at, stage) pair plus index is stable enough for a list that only
    // ever grows, never reorders.
    case "stage_change": return `stage-${entry.at}-${entry.stage}-${index}`;
  }
}

function ActivityEntryRow({ entry }: { entry: ActivityFeedEntry }) {
  switch (entry.type) {
    case "submission":
      return (
        <li className={styles.row}>
          <RowHeader badgeText="Homeowner" badgeClassName={ui.badgeInfo} at={entry.at} />
          <p className={styles.rowContent}>
            {entry.homeownerName
              ? `${entry.homeownerName} submitted this design request.`
              : "This design request was submitted."}
          </p>
        </li>
      );

    case "version":
      return (
        <li className={styles.row}>
          <RowHeader
            badgeText={entry.authorKind === "homeowner" ? "Homeowner" : "Builder"}
            badgeClassName={entry.authorKind === "homeowner" ? ui.badgeInfo : ui.badgeBuilder}
            at={entry.at}
          />
          <p className={styles.rowContent}>
            {entry.label ? `Saved a design version: “${entry.label}”.` : "Saved a design version."}{" "}
            <a href="#version-history" className={styles.feedLink}>View in version history</a>
          </p>
        </li>
      );

    case "note":
      return (
        <li className={styles.row}>
          <RowHeader
            badgeText={entry.authorRole === "owner" ? "Owner" : "Rep"}
            badgeClassName={entry.authorRole === "owner" ? ui.badgeOk : ui.badgeTan}
            at={entry.at}
          />
          <p className={styles.rowContent}>{entry.text}</p>
        </li>
      );

    case "stage_change":
      return (
        <li className={styles.row}>
          <RowHeader badgeText="Builder" badgeClassName={ui.badgeBuilder} at={entry.at} />
          <p className={styles.rowContent}>Moved to “{STAGE_LABELS[entry.stage]}”.</p>
        </li>
      );
  }
}

function RowHeader({ badgeText, badgeClassName, at }: {
  badgeText: string;
  badgeClassName: string;
  at: string | null;
}) {
  return (
    <div className={styles.rowHeader}>
      <span className={`${ui.badge} ${badgeClassName}`}>{badgeText}</span>
      <span className={styles.timestamp}>{formatEntryTimestamp(at)}</span>
    </div>
  );
}

// Mirrors VersionHistoryPanel's own formatVersionTimestamp exactly (same
// locale/format options, same "Unknown date" fallback for a null/unparseable
// timestamp — see DesignVersionEntry.createdAt's doc comment in api.ts for
// why a version's `at` can legitimately be null) so a version's timestamp
// reads identically whether seen here or in that panel.
function formatEntryTimestamp(at: string | null): string {
  if (!at) return "Unknown date";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });
}
